import React, { useState, useCallback, useEffect } from 'react';
import { apiService } from '../services/apiService';
import { Mail, Save, Check, AlertCircle, Eye, EyeOff, Briefcase, FileText } from 'lucide-react';

const C = {
  bg: 'var(--c-bg)',
  bg1: 'var(--c-bg-1)',
  bg2: 'var(--c-bg-2)',
  surface: 'var(--c-surface)',
  border: 'var(--c-border)',
  primary: 'var(--c-primary)',
  accent: 'var(--c-accent)',
  text: 'var(--c-text)',
  text2: 'var(--c-text-2)',
  text3: 'var(--c-text-3)',
  red: 'var(--c-red)',
  yellow: 'var(--c-yellow)',
  blue: 'var(--c-blue)',
  purple: 'var(--c-purple)',
};

const Settings = () => {
  const [emailConfig, setEmailConfig] = useState({
    smtp_user: '',
    smtp_password: '',
    smtp_host: 'smtp.gmail.com',
    smtp_port: '587',
    from_name: '',
    from_email: ''
  });

  const [campaignContext, setCampaignContext] = useState({
    sender_name: '',
    company_name: '',
    company_description: '',
    campaign_context: '',
    email_signature: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingCampaign, setIsSavingCampaign] = useState(false);
  const [showSignaturePreview, setShowSignaturePreview] = useState(false);
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((type, message) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, type, message }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 5000);
  }, []);

  const loadEmailConfig = useCallback(async () => {
    try {
      const response = await apiService.getEmailConfig();
      if (response.status === 'success' && response.config) {
        setEmailConfig(prev => ({
          ...prev,
          ...response.config,
          smtp_password: ''
        }));
      }
    } catch (error) {
      console.error('Error loading email config:', error);
    }
  }, []);

  const loadCampaignContext = useCallback(async () => {
    try {
      const response = await apiService.getCampaignContext();
      if (response.status === 'success' && response.config) {
        setCampaignContext(prev => ({ ...prev, ...response.config }));
      }
    } catch (error) {
      console.error('Error loading campaign context:', error);
    }
  }, []);

  const handleInputChange = (field, value) => {
    setEmailConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleCampaignChange = (field, value) => {
    setCampaignContext(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveEmailConfig = async () => {
    setIsSaving(true);
    try {
      if (!emailConfig.smtp_user) { showToast('error', 'Email address is required'); setIsSaving(false); return; }
      if (!emailConfig.smtp_password) { showToast('error', 'App password is required'); setIsSaving(false); return; }
      await apiService.saveEmailConfig(emailConfig);
      showToast('success', 'Email settings saved!');
      setEmailConfig(prev => ({ ...prev, smtp_password: '' }));
    } catch (error) {
      showToast('error', 'Failed to save email settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveCampaignContext = async () => {
    setIsSavingCampaign(true);
    try {
      await apiService.saveCampaignContext(campaignContext);
      showToast('success', 'Campaign context saved!');
    } catch (error) {
      showToast('error', 'Failed to save campaign context');
    } finally {
      setIsSavingCampaign(false);
    }
  };

  useEffect(() => { loadEmailConfig(); loadCampaignContext(); }, [loadEmailConfig, loadCampaignContext]);

  const s = {
    page: { minHeight: 'calc(100vh - 64px)', background: C.bg, padding: '32px 24px' },
    card: { background: C.bg1, border: `1px solid ${C.border}`, borderRadius: 16, overflow: 'hidden', maxWidth: 800, margin: '0 auto', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' },
    hdr: { padding: '24px', borderBottom: `1px solid ${C.border}`, background: 'rgba(139,92,246,0.04)' },
    body: { padding: '32px' },
    infoBox: { background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 12, padding: 18, marginBottom: 24 },
    label: { color: C.text2, fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 8 },
    input: { width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, color: C.text, padding: '12px 14px', fontSize: 14, fontFamily: 'Inter, sans-serif', outline: 'none', boxSizing: 'border-box' },
    textarea: { width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, color: C.text, padding: '12px 14px', fontSize: 14, fontFamily: 'Inter, sans-serif', outline: 'none', boxSizing: 'border-box', minHeight: 120, resize: 'vertical', lineHeight: 1.6 },
    btnPrim: { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '14px', background: 'linear-gradient(135deg, #8B5CF6, #3B82F6)', color: '#fff', fontWeight: 700, fontSize: 14, border: 'none', borderRadius: 10, cursor: 'pointer', fontFamily: 'Inter, sans-serif', boxShadow: '0 4px 16px rgba(139,92,246,0.25)' },
  };

  return (
    <div style={s.page}>
      {/* Email Configuration Card */}
      <div style={s.card}>
        <div style={s.hdr}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Mail size={28} color={C.purple} />
            <div>
              <h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 22, fontWeight: 700, color: C.text, margin: 0 }}>Email Configuration</h1>
              <p style={{ color: C.text2, fontSize: 13, margin: '2px 0 0' }}>Configure your SMTP settings for sending emails</p>
            </div>
          </div>
        </div>

        <div style={s.body}>
          <div style={s.infoBox}>
            <div style={{ display: 'flex', gap: 12 }}>
              <AlertCircle size={20} color={C.blue} style={{ marginTop: 2, flexShrink: 0 }} />
              <div>
                <p style={{ fontWeight: 700, color: C.text, fontSize: 14, marginBottom: 6 }}>Gmail App Password Required</p>
                <p style={{ fontSize: 13, color: C.text2, lineHeight: 1.5 }}>To send emails, you must use an App-Specific Password from your Google Account security settings.</p>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div>
              <label style={s.label}>Your Gmail Address *</label>
              <input type="email" value={emailConfig.smtp_user} onChange={e => handleInputChange('smtp_user', e.target.value)} style={s.input} placeholder="your-email@gmail.com" />
            </div>

            <div>
              <label style={s.label}>App-Specific Password *</label>
              <div style={{ position: 'relative' }}>
                <input type={showPassword ? 'text' : 'password'} value={emailConfig.smtp_password} onChange={e => handleInputChange('smtp_password', e.target.value)} style={{ ...s.input, paddingRight: 44 }} placeholder="xxxx xxxx xxxx xxxx" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: C.text3 }}>
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <label style={s.label}>Sender Name</label>
              <input type="text" value={emailConfig.from_name} onChange={e => handleInputChange('from_name', e.target.value)} style={s.input} placeholder="Your Name" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <label style={s.label}>SMTP Host</label>
                <input type="text" value={emailConfig.smtp_host} onChange={e => handleInputChange('smtp_host', e.target.value)} style={s.input} />
              </div>
              <div>
                <label style={s.label}>SMTP Port</label>
                <input type="text" value={emailConfig.smtp_port} onChange={e => handleInputChange('smtp_port', e.target.value)} style={s.input} />
              </div>
            </div>

            <button onClick={handleSaveEmailConfig} disabled={isSaving} style={{ ...s.btnPrim, opacity: isSaving ? 0.6 : 1 }}>
              {isSaving ? 'Saving...' : <><Save size={18} /> Save Email Configuration</>}
            </button>
          </div>
        </div>
      </div>

      {/* Campaign Context Card */}
      <div style={{ ...s.card, marginTop: 24 }}>
        <div style={s.hdr}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Briefcase size={28} color={C.purple} />
            <div>
              <h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 22, fontWeight: 700, color: C.text, margin: 0 }}>Campaign Context</h1>
              <p style={{ color: C.text2, fontSize: 13, margin: '2px 0 0' }}>Customize how AI generates your outreach emails</p>
            </div>
          </div>
        </div>

        <div style={s.body}>
          <div style={s.infoBox}>
            <div style={{ display: 'flex', gap: 12 }}>
              <AlertCircle size={20} color={C.blue} style={{ marginTop: 2, flexShrink: 0 }} />
              <div>
                <p style={{ fontWeight: 700, color: C.text, fontSize: 14, marginBottom: 6 }}>AI Email Personalization</p>
                <p style={{ fontSize: 13, color: C.text2, lineHeight: 1.5 }}>These settings control how AI generates personalized emails for your campaigns. The more context you provide, the better the AI tailors each email.</p>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <label style={s.label}>Sender Name</label>
                <input type="text" value={campaignContext.sender_name} onChange={e => handleCampaignChange('sender_name', e.target.value)} style={s.input} placeholder="e.g. Stefan" />
              </div>
              <div>
                <label style={s.label}>Company Name</label>
                <input type="text" value={campaignContext.company_name} onChange={e => handleCampaignChange('company_name', e.target.value)} style={s.input} placeholder="e.g. Passionfruit Careers" />
              </div>
            </div>

            <div>
              <label style={s.label}>Company Description</label>
              <textarea value={campaignContext.company_description} onChange={e => handleCampaignChange('company_description', e.target.value)} style={s.textarea} placeholder="Brief description of what your company does, key features, and value proposition..." rows={3} />
            </div>

            <div>
              <label style={s.label}>Campaign Pitch / Context</label>
              <textarea value={campaignContext.campaign_context} onChange={e => handleCampaignChange('campaign_context', e.target.value)} style={{ ...s.textarea, minHeight: 180 }} placeholder="Describe your full offering, target audience, key benefits, pricing hooks, and what you want the AI to emphasize in outreach emails. The more detail, the better the AI personalizes each email." rows={8} />
            </div>

            <button onClick={handleSaveCampaignContext} disabled={isSavingCampaign} style={{ ...s.btnPrim, opacity: isSavingCampaign ? 0.6 : 1 }}>
              {isSavingCampaign ? 'Saving...' : <><Save size={18} /> Save Campaign Context</>}
            </button>
          </div>
        </div>
      </div>

      {/* Email Signature Card */}
      <div style={{ ...s.card, marginTop: 24 }}>
        <div style={s.hdr}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <FileText size={28} color={C.purple} />
            <div>
              <h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 22, fontWeight: 700, color: C.text, margin: 0 }}>Email Signature</h1>
              <p style={{ color: C.text2, fontSize: 13, margin: '2px 0 0' }}>Custom signature appended to all outgoing emails</p>
            </div>
          </div>
        </div>

        <div style={s.body}>
          <div style={s.infoBox}>
            <div style={{ display: 'flex', gap: 12 }}>
              <AlertCircle size={20} color={C.blue} style={{ marginTop: 2, flexShrink: 0 }} />
              <div>
                <p style={{ fontWeight: 700, color: C.text, fontSize: 14, marginBottom: 6 }}>HTML Supported</p>
                <p style={{ fontSize: 13, color: C.text2, lineHeight: 1.5 }}>You can use HTML for formatting (bold, links, images). This replaces the default email signature on all outgoing emails.</p>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div>
              <label style={s.label}>Signature (HTML)</label>
              <textarea
                value={campaignContext.email_signature}
                onChange={e => handleCampaignChange('email_signature', e.target.value)}
                style={{ ...s.textarea, minHeight: 180, fontFamily: "'Fira Code', 'Courier New', monospace", fontSize: 13 }}
                placeholder={'Best regards,\nJohn Doe\n\nFounder & CEO\nAcme Corp\n<a href="https://acme.com">acme.com</a>'}
                rows={8}
              />
            </div>

            <button
              onClick={() => setShowSignaturePreview(!showSignaturePreview)}
              style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 16px', color: C.text2, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'Inter, sans-serif', width: 'fit-content' }}
            >
              <Eye size={16} />
              {showSignaturePreview ? 'Hide Preview' : 'Show Preview'}
            </button>

            {showSignaturePreview && campaignContext.email_signature && (
              <div style={{ background: '#ffffff', border: `1px solid ${C.border}`, borderRadius: 10, padding: 20, color: '#333', fontFamily: 'Arial, sans-serif', fontSize: 14, lineHeight: 1.6 }}>
                <div style={{ borderTop: '1px solid #ddd', paddingTop: 16 }}>
                  <div dangerouslySetInnerHTML={{ __html: campaignContext.email_signature }} />
                </div>
              </div>
            )}

            <button onClick={handleSaveCampaignContext} disabled={isSavingCampaign} style={{ ...s.btnPrim, opacity: isSavingCampaign ? 0.6 : 1 }}>
              {isSavingCampaign ? 'Saving...' : <><Save size={18} /> Save Signature</>}
            </button>
          </div>
        </div>
      </div>

      {/* Feature Access Footer */}
      <div style={{ ...s.card, marginTop: 24 }}>
        <div style={{ padding: '24px', background: 'var(--c-primary-glow)' }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 12 }}>Feature Access</h3>
          {[
            'Send AI-generated emails to scraped profiles',
            'Monitor inbox for replies and auto-analyze',
            'Manage sales pipeline from one place'
          ].map(item => (
            <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <Check size={14} color={C.primary} />
              <span style={{ fontSize: 13, color: C.text2 }}>{item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Toasts */}
      <div style={{ position: 'fixed', bottom: 24, right: 24, display: 'flex', flexDirection: 'column', gap: 10, zIndex: 200 }}>
        {toasts.map(t => (
          <div key={t.id} style={{ padding: '12px 18px', borderRadius: 12, fontSize: 13, fontWeight: 500, background: t.type === 'error' ? 'rgba(239,68,68,0.08)' : 'rgba(0,179,134,0.08)', border: `1px solid ${t.type === 'error' ? 'rgba(239,68,68,0.15)' : 'rgba(0,179,134,0.15)'}`, color: t.type === 'error' ? C.red : C.primary, boxShadow: 'var(--shadow-elevated)', backdropFilter: 'blur(10px)' }}>
            {t.message}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Settings;
